<template>
  <page layoutClass="jobs-index" :err="err">
    <!-- Header -->
    <b-row class="my-3">
      <b-col cols="12" sm="7">
        <h2 class="m-0">{{ $t('jobs.listing-title') }}</h2>
      </b-col>
      <b-col cols="12" sm="5" class="d-flex flex-row justify-content-end align-items-center" v-if="isRecruiter && showAddJobsButton">
        <!-- New Job Button -->
        <b-button size="md" variant="outline-primary" :to="{ name: 'JobNew' }">
          <font-awesome-icon icon="plus"/>
          <span class="d-none d-sm-inline-block">{{ $t('ctas.plus-new') }}</span>
        </b-button>
        <!-- Add Job by URL Button -->
        <b-button size="md" variant="outline-primary" v-b-modal.new-job-by-url-modal>
          <font-awesome-icon icon="plus"/>
          <span class="d-none d-sm-inline-block">Add Job</span>
        </b-button>
      </b-col>
    </b-row>

    <jobs-filter layout="page" :loading="$apollo.loading || !jobs"
      :where="filterJobsWhere" @whereUpdated="filterJobsWhereUpdated" :whereCustomFields="jobs ? jobs.jobAggs : null"
      :results="jobs ? jobs.jobConnections : null" @pageUpdated="filterJobsPageUpdated"
      :order="filterJobsOrder" @orderUpdated="filterJobsOrderUpdated"/>

    <!-- Add Job by URL Modal -->
    <b-modal
      id="new-job-by-url-modal"
      size="xl"
      no-close-on-backdrop
      hide-footer
      title="Create Job"
      v-show="this.isRecruiter"
    >
      <content-fetch @input="createNewJob($event)" />
    </b-modal>

  </page>
</template>

<script>
import base64 from 'base-64';
import ms from 'ms';
import enumsMixin from '@src/mixins/enums';
import JobsFilter from '@/components/jobs/FilterContainer';
import Page from '@/components/Page';
import ContentFetch from '@/components/misc/ContentFetch.vue';
import JobsIndexQuery from '@/graphql/queries/JobsIndex.gql';
import { mapRoot } from '@src/mixins/root';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import gql from 'graphql-tag';

const NewJob = gql`
  mutation JobNew($create: CreateJobInput!) {
    createJob(create: $create) {
      ...JobDetails
    }
  }
  ${JobDetailsFragment}
`;

export default {
  components: {
    Page,
    JobsFilter,
    ContentFetch
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    err: null,
    filterJobsWhere: {
      // matches: { gt: 0 },
      NOT: {
        isInternal: true
      },
      project: null,
      status: 'ACTIVE',
    },
    filterJobsPage: 1,
    filterJobsLimit: 20,
    filterJobsOrder: 'CREATED_DESC',
    // filterMatchesRelevance: 1.0,
  }),
  created() {
    // const { STRONG } = this.contactRelevanceBoundaries;
    // this.filterMatchesRelevance = STRONG;

    if (this.company && this.company.sitePreferences && this.isLoggedIn) {
      const { company: { sitePreferences } } = this;
      this.filterJobsOrder = (this.isRecruiter ? sitePreferences.defaultRecruiterJobOrderBy
        : sitePreferences.defaultJobOrderBy) || this.filterJobsOrder;
    }

    if (this.$route && this.$route.query && this.$route.query.filter) {
      const { where, order, page, limit } = JSON.parse(base64.decode(this.$route.query.filter));
      this.$log.debug({ where, order, page, limit });

      if (typeof where === 'object') {
        this.filterJobsWhere = { ...this.filterJobsWhere, ...where };
      }
      if (typeof order === 'string') {
        this.filterJobsOrder = order;
      }
      if (typeof page === 'number') {
        this.filterJobsPage = page;
      }
      if (typeof limit === 'number') {
        this.filterJobsLimit = limit;
      }
    }
  },
  apollo: {
    jobs: {
      query: JobsIndexQuery,
      variables() {
        const { filterJobsWhere: where, filterJobsOrder: order, filterJobsPage: page, filterJobsLimit: limit } = this;
        return { isEmployee: this.isLoggedIn && !this.isRecruiter, where, order, page, limit };
      },
      update(data) {
        if (!this.isLoggedIn || this.isRecruiter) {
          const { company: { jobAggs, jobConnections } = {} } = data;
          return { jobAggs, jobConnections };
        } else {
          const { company: { jobAggs } = {}, user: { jobConnections } = {} } = data;
          return { jobAggs, jobConnections };
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      deep: true,
      throttle: ms('1s'),
    },
  },
  metaInfo() {
    return {
      title: this.$t('jobs.listing-title'),
    };
  },
  computed: {
    ...mapRoot([ 'company', 'isLoggedIn', 'isRecruiter' ]),
    showAddJobsButton() {
      const { showAddJobsButton = false } = this.company.sitePreferences;
      return showAddJobsButton;
    },
  },
  methods: {
    filterJobsWhereUpdated(where) {
      if (JSON.stringify(this.filterJobsWhere) !== JSON.stringify(where)) {
        this.filterJobsWhere = where;
        this.filterJobsPage = 1;

        this.updateRouteFilter({ where });
        this.$scrollTo('body');
      }
    },
    filterJobsOrderUpdated(order) {
      this.filterJobsOrder = order;
      this.filterJobsPage = 1;

      this.updateRouteFilter({ page: undefined, order });
      this.$scrollTo('body');
    },
    filterJobsPageUpdated(page) {
      this.filterJobsPage = page;
      this.updateRouteFilter({ page });
      this.$scrollTo('body');
    },
    updateRouteFilter(values) {
      const existing = this.$route && this.$route.query ? this.$route.query.filter : null;
      this.$router.push({
        ...this.$route,
        query: {
          filter: base64.encode(JSON.stringify({
            ...(existing ? JSON.parse(base64.decode(existing)) : {}),
            ...values
          })),
        },
      });
    },
    async createNewJob(job) {
      try {
        await this.$apollo.mutate({
          mutation: NewJob,
          variables: { create: job },
          update: (store, response) => {

            console.log('Store: ', store);
            console.log('Response: ', response);

            const {
              filterJobsWhere: where,
              filterJobsOrder: order,
              filterJobsPage: page,
              filterJobsLimit: limit
            } = this;

            const data = store.readQuery({
              query: JobsIndexQuery,
              variables: {
                isEmployee: this.isLoggedIn && !this.isRecruiter, where, order, page, limit
              }
            });

            if (data.company.jobConnections && Array.isArray(data.company.jobConnections.edges) && data.company.jobConnections.edges.length > 0) {
              if (data.company.jobConnections.total > limit) {
                data.company.jobConnections.edges.pop();
              }
              data.company.jobConnections.edges.unshift({ entry: response.data.createJob, __typename: 'JobEdge' });
              data.company.jobConnections.total++;
              console.log('IF 1');
            } else if (data.company.jobConnections) {
              data.company.jobConnections.edges = [ { entry: response.data.createJob, __typename: 'JobEdge' } ];
              data.company.jobConnections.total++;
              console.log('IF 2');
            } else {
              data.company.jobConnections = {
                edges: [ { entry: response.data.createJob, __typename: 'JobEdge' } ],
                total: 1
              };
              console.log('IF 3');
            };

            store.writeQuery({
              query: JobsIndexQuery,
              variables: {
                isEmployee: this.isLoggedIn && !this.isRecruiter, where, order, page, limit
              },
              data
            });

            console.log('Data: ', data);
          }
        });
      } catch (err) {
        console.log(err);
      }
    }
  },
};
</script>
<style lang="scss">
.page .jobs-index {

  h2 {
    margin-bottom: 0.75em;
  }

}
</style>



// WEBPACK FOOTER //
// src/views/jobs/jobsIndex.vue